import { blockedContactAction } from '../../../Actions/BlockAction';
import { ChatMessageHistoryDataAction, UpdateFavouriteStatus } from '../../../Actions/ChatHistory';
import { WebChatConnectionState } from '../../../Actions/ConnectionState';
import { GroupDataUpdateAction } from '../../../Actions/GroupsAction';
import { messageInfoAction } from '../../../Actions/MessageActions';
import { RosterPermissionAction } from '../../../Actions/RosterActions';
import { chatSeenPendingMsg } from '../../../Actions/SingleChatMessageActions';
import { StarredMessagesList } from '../../../Actions/StarredAction';
import { formatToArrayofJid, setContactWhoBleckedMe } from '../../../Helpers/Chat/BlockContact';
import {
    arrayToObject,
    getAllStarredMessages,
    getUnreadData,
    handleArchivedChats,
    handleUserSettings,
    isActiveConversationUserOrGroup,
    isGroupChat,
} from '../../../Helpers/Chat/ChatHelper';
import { applyMutedChats } from '../../../Helpers/Chat/muteState';
import { CHAT_TYPE_GROUP, CONNECTION_STATE_CONNECTING } from '../../../Helpers/Chat/Constant';
import { formatUserIdToJid, getIdFromJid, isLocalUser } from '../../../Helpers/Chat/User';
import { hydrateFromIDB } from '../../../Helpers/idbHydration';
import { runStartupCleanup } from '../../../Helpers/Chat/DisappearScheduler';
import { isAppOnline, updateMuteSettings } from '../../../Helpers/Utility';
import { Store } from '../../../store';
import { REACT_APP_CONTACT_SYNC } from '../../processENV';
import SDK from '../../SDK';
import { encryptAndStoreInLocalStorage, getFromLocalStorageAndDecrypt } from '../../WebChat/WebChatEncryptDecrypt';
import { setConnectionStatus } from '../Common/FileUploadValidation';
import { PrivacySettingsAction } from '../../../Actions/PrivacySettingsAction';
import { SyncProgressAction } from '../../../Actions/SyncProgressAction';
import logger from '../../../Helpers/logger';
import { restoreOutbox } from '../../../features/outbox/outboxQueue';
import { syncAllChats, syncReconnect } from './synchronisation/ChatSync';
import { syncAllGroups } from './synchronisation/GroupSync';

const handleBlockMethods = async () => {
    const userIBlockedRes = await SDK.getUsersIBlocked();
    if (userIBlockedRes && userIBlockedRes.statusCode === 200) {
        const jidArr = formatToArrayofJid(userIBlockedRes.data);
        Store.dispatch(blockedContactAction(jidArr));
    }

    const userBlockedMeRes = await SDK.getUsersWhoBlockedMe();
    if (userBlockedMeRes && userBlockedMeRes.statusCode === 200) {
        const jidArr = formatToArrayofJid(userBlockedMeRes.data);
        setContactWhoBleckedMe(jidArr);
    }
};

const getAndUpdateChatMessages = async (chatId, chatType, rowId, lastMsgId = '') => {
    const unreadData = getUnreadData();
    const hasUnread = unreadData[chatId] && unreadData[chatId].count > 0;
    const chatMessageRes = await SDK.getChatMessages({
        toJid: formatUserIdToJid(chatId, chatType),
        ...(hasUnread && { position: 'up', lastMessageId: lastMsgId, includeMsgObj: true }),
        sendSeen: false,
    });
    if (chatMessageRes && chatMessageRes.statusCode === 200) {
        chatMessageRes.data.reconnectFetch = 1;
        chatMessageRes.chatType = chatType;
        delete chatMessageRes.statusCode;
        delete chatMessageRes.message;
        if (chatId === getIdFromJid(chatMessageRes.userJid || chatMessageRes.groupJid)) {
            Store.dispatch(ChatMessageHistoryDataAction(chatMessageRes));
            const groupId = isGroupChat(chatType) ? chatId : '';
            const { privacySettingsData } = Store.getState();
            chatMessageRes.data.forEach(el => {
                if (!isLocalUser(el.publisherId) && el.rowId !== rowId) {
                    if (isActiveConversationUserOrGroup(chatId)) {
                        const toUserId = isGroupChat(el.chatType) ? el.publisherId : el.fromUserId;
                        const messageReceiptStatus = el.msgBody?.receipt_status === '1';
                        const userSeenReceiptEnabled = privacySettingsData.seenReceipt === 1;
                        const finalSeenReceipt = messageReceiptStatus && userSeenReceiptEnabled ? '1' : '0';
                        SDK.sendSeenStatus(formatUserIdToJid(toUserId), el.msgId, groupId, finalSeenReceipt);
                    } else {
                        Store.dispatch(chatSeenPendingMsg(el));
                    }
                }
            });
        }
    }
};

const checkIfConversationRefreshNeeded = (newRecent, oldRecent) => {
    const { msgStatus: newMsgStatus, msgId: newMsgId } = newRecent;
    const { msgStatus: oldMsgStatus, msgId: oldMsgId } = oldRecent;
    return newMsgStatus !== oldMsgStatus || newMsgId !== oldMsgId;
};

const handleChatHistoryUpdate = async (recentChatArr, oldRecentChatData) => {
    const { chatConversationHistory: { data } = {} } = Store.getState();
    const chatHistoryIds = Object.keys(data);
    const recentChatArrNew = recentChatArr.reverse();
    const oldRecentChatObj = arrayToObject(oldRecentChatData, 'fromUserId');

    for (const newRecentChat of recentChatArrNew) {
        const { fromUserId: chatId, msgId: recentMsgId, chatType, unreadMessageId } = newRecentChat;
        const oldRecentChat = oldRecentChatObj[chatId];

        if (chatHistoryIds.includes(chatId)) {
            const isRefreshNeeded = checkIfConversationRefreshNeeded(newRecentChat, oldRecentChat);
            // If Message Status Changed OR Recent MsgId differs from Unread MsgId - Refresh Conversation History
            if (isRefreshNeeded || isAppOnline() || recentMsgId !== unreadMessageId) {
                getAndUpdateChatMessages(chatId, chatType, '', unreadMessageId);
            }
        }
    }
};

const handleFavouriteMessages = async () => {
    const favResult = await SDK.getAllFavouriteMessages();

    // Here Updating the Coversation History If there is any new added/removed Favourite Messages are there when offline
    if (favResult.statusCode === 200) {
        const starredMessages = getAllStarredMessages();
        const addedFavs = favResult.data.filter(
            ({ msgId: id1 }) => !starredMessages.some(({ msgId: id2 }) => id2 === id1),
        );
        if (addedFavs.length) {
            addedFavs.forEach(msg => {
                const res = {
                    fromUserId: msg.fromUserId,
                    msgId: msg.msgId,
                    favouriteStatus: 1,
                };
                Store.dispatch(UpdateFavouriteStatus(res));
            });
        }
        const removedFavs = starredMessages.filter(
            ({ msgId: id1 }) => !favResult.data.some(({ msgId: id2 }) => id2 === id1),
        );
        if (removedFavs.length) {
            removedFavs.forEach(msg => {
                const res = {
                    fromUserId: msg.fromUserId,
                    msgId: msg.msgId,
                    favouriteStatus: 0,
                };
                Store.dispatch(UpdateFavouriteStatus(res));
            });
        }
        Store.dispatch(StarredMessagesList(favResult.data));
    }
};

const handleMentionedMessage = async () => {
    const state = Store.getState();
    if (state.selectedMessageInfoReducer?.data?.msgId) {
        Store.dispatch(messageInfoAction(state.selectedMessageInfoReducer?.data?.msgId, true));
        SDK.getGroupMsgInfo(state.groupsMemberListData.data.groupJid, state.selectedMessageInfoReducer.data.msgId);
    }
};

export async function login(mobileLogin = false) {
    try {
        if (getFromLocalStorageAndDecrypt('auth_user')) {
            const overallStartTime = performance.now();
            let decryptResponse = getFromLocalStorageAndDecrypt('auth_user');
            const tokenResult = await SDK.getUserToken(decryptResponse.username, decryptResponse.password);
            if (tokenResult.statusCode === 200) {
                await SDK.getUserProfile(formatUserIdToJid(decryptResponse.username));
                encryptAndStoreInLocalStorage('token', tokenResult.userToken);
                Store.dispatch(SyncProgressAction(0)); // Start sync
                const fetchPrivacySettingData = await SDK.getUserPrivacySettings();
                if (fetchPrivacySettingData && fetchPrivacySettingData.statusCode === 200) {
                    const hideProfile = fetchPrivacySettingData.privacySettings?.hideProfile ?? 0;
                    if (mobileLogin) {
                        const response = await SDK.updateUserPrivacySettings('hideProfile', hideProfile.toString());
                        Store.dispatch(
                            PrivacySettingsAction({
                                ...fetchPrivacySettingData.privacySettings,
                                hideProfile,
                            }),
                        );
                    } else {
                        Store.dispatch(
                            PrivacySettingsAction({
                                ...fetchPrivacySettingData.privacySettings,
                                hideProfile,
                            }),
                        );
                    }
                }
                Store.dispatch(SyncProgressAction(15)); // Privacy settings complete
            }
            await syncAllGroups();
            Store.dispatch(SyncProgressAction(35)); // Groups complete
            if (REACT_APP_CONTACT_SYNC) {
                await SDK.getFriendsList();
            } else {
                Store.dispatch(RosterPermissionAction(1));
            }
            Store.dispatch(SyncProgressAction(35)); // Contacts complete
            // Must run after syncAllChats: these dispatch flag updates (mute/archive/pin)
            // that map over the recent-chat list, which syncAllChats is what populates.
            await syncAllChats({ progressRange: [35, 100] });
            handleUpdateStatus();
            const totalDuration = ((performance.now() - overallStartTime) / 1000).toFixed(2);
            logger.debug(`[Reconnect] 🚀 Full Login/Sync process took: ${totalDuration}s`);
        }
    } catch (error) {
        logger.debug('reconnect error :>> ', error);
    }
}

export function reconnect() {
    // Update connection status
    encryptAndStoreInLocalStorage('connection_status', CONNECTION_STATE_CONNECTING);
    setConnectionStatus(CONNECTION_STATE_CONNECTING);
    Store.dispatch(WebChatConnectionState(CONNECTION_STATE_CONNECTING));
}

const handleUpdateStatus = async () => {
    //    SDK.sendIpUpdate();
    updateMuteStatus();
    handleFavouriteMessages();
    handleUserSettings();
    handleArchivedChats();
    handleBlockMethods();
    handleMentionedMessage();
    // Messages queued while offline go out once the session is back.
    Store.dispatch(restoreOutbox());
};

const updateProfile = async (recentChatArr = []) => {
    if (REACT_APP_CONTACT_SYNC) {
        SDK.getFriendsList();
    }
    const groupIds = recentChatArr.map(item => item.chatType === 'groupchat' && item.fromUserId);
    const { users: groups = [] } = await SDK.getUsersProfile(groupIds);
    groups.forEach(res => {
        const obj = {
            groupJid: formatUserIdToJid(res.userId, CHAT_TYPE_GROUP),
            msgType: 'profileUpdated',
            groupProfile: {
                nickName: res.nickName,
                image: res.image,
                thumbImage: res.thumbImage,
            },
        };
        Store.dispatch(GroupDataUpdateAction(obj));
    });
};

export const serverReconnect = async () => {
    // Restore cached data immediately so the UI isn't blank during reconnect.
    await hydrateFromIDB();
    runStartupCleanup();

    // setTimeout(() => {
    //     SessionManager.check();
    // }, 3000);
    await syncAllGroups();
    const decryptResponse = getFromLocalStorageAndDecrypt('auth_user');
    if (decryptResponse) {
        await SDK.getUserProfile(formatUserIdToJid(decryptResponse.username));
        const fetchPrivacySettingData = await SDK.getUserPrivacySettings();
        if (fetchPrivacySettingData && fetchPrivacySettingData.statusCode === 200) {
            Store.dispatch(PrivacySettingsAction(fetchPrivacySettingData.privacySettings));
        }
    }
    handleUpdateStatus();

    await syncReconnect();
};

const updateMuteStatus = async () => {
    const mutedResponse = await SDK.getMutedUsers();
    if (mutedResponse?.statusCode === 200) {
        const { muteSettingStatus = false } = mutedResponse.data || {};
        updateMuteSettings(muteSettingStatus);
        applyMutedChats(mutedResponse.data);
    }
};
